import { useForm, FormProvider, useFormContext, DefaultValues, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../Loading';
import { FormValues, Props, FormRenderProps } from './types';

export function Form<T extends FormValues>({
  children: Component,
  onSubmit,
  initialValues,
  validationSchema,
  withLoading = true,
  afterSubmitRoute,
}: Props<T>) {
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();

  const methods = useForm<T>({
    defaultValues: initialValues as DefaultValues<T>,
    resolver: validationSchema
      ? (yupResolver(validationSchema) as unknown as Resolver<T>)
      : undefined,
    mode: 'onChange',
  });

  const submit = methods.handleSubmit(async (values) => {
    if (withLoading) showLoading();
    await onSubmit(values as T);
    if (withLoading) hideLoading();
    methods.reset();
    if (afterSubmitRoute) navigate(afterSubmitRoute);
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={submit}>
        <Renderer Component={Component} />
      </form>
    </FormProvider>
  );
}

function Renderer<T extends FormValues>({
  Component,
}: {
  Component: (props: FormRenderProps<T>) => JSX.Element;
}) {
  const { watch, formState } = useFormContext<T>();
  const values = watch();
  return (
    <Component
      values={values as T}
      errors={formState.errors as FormRenderProps<T>['errors']}
      touched={formState.touchedFields as FormRenderProps<T>['touched']}
      isValid={formState.isValid}
      isSubmitting={formState.isSubmitting}
      isDirty={formState.isDirty}
    />
  );
}
